import { Box, Checkbox, FormControlLabel } from "@mui/material";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import type { FilterDraft, FilterField, FilterOp } from "@/components/BlotterFilterBar";
import { visibleOptions } from "@/utils/blotterFilter-helper";

interface BlotterFilterPanelProps {
  field: FilterField;
  initialDraft: FilterDraft;
  onApply: (draft: FilterDraft) => void;
  onCancel: () => void;
}

const OPS: { op: FilterOp; label: string }[] = [
  { op: "is", label: "Is" },
  { op: "isnot", label: "Is not" },
];

// Matches the design's `.sf-op-btn` / `.sf-op-btn.active`.
function opButtonStyle(isActive: boolean): CSSProperties {
  return {
    flex: 1,
    border: `1px solid ${isActive ? "var(--color-dark-dust)" : "var(--color-smoke)"}`,
    background: isActive ? "var(--color-clear-dust)" : "var(--color-white)",
    color: isActive ? "var(--color-dark-dust)" : "var(--color-rodeo-black)",
    borderRadius: 7,
    padding: 6,
    fontFamily: "var(--font-lato)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  };
}

// Matches the design's `.sf-pop-btn`.
const footButton: CSSProperties = {
  borderRadius: 7,
  padding: "7px 14px",
  fontFamily: "var(--font-lato)",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

/**
 * The opt-in value panel for one multi-select field: Is / Is not, a search box, checkboxes and
 * Cancel / Apply. Holds a local draft; nothing reaches the blotter until Apply.
 */
export default function BlotterFilterPanel({
  field,
  initialDraft,
  onApply,
  onCancel,
}: BlotterFilterPanelProps) {
  const [draft, setDraft] = useState<FilterDraft>(initialDraft);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const shown = visibleOptions(field.options, query);

  // Picking a field unmounts the focused menu item; land focus in the search box instead of <body>.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  function toggle(value: string) {
    setDraft((d) => ({
      ...d,
      values: d.values.includes(value) ? d.values.filter((v) => v !== value) : [...d.values, value],
    }));
  }

  return (
    <Box
      // MUI's MenuList eats keydown (typeahead, arrows, Tab) from the menu's children, which
      // would swallow typing in the search box. Escape is let through so the Menu still closes.
      onKeyDown={(e) => {
        if (e.key !== "Escape") e.stopPropagation();
      }}
      sx={{ px: "6px", pb: "2px", minWidth: 248, maxWidth: 300 }}
    >
      <fieldset
        aria-label={`${field.label} match`}
        style={{
          display: "flex",
          gap: 6,
          padding: "0 8px 8px",
          margin: 0,
          border: "none",
          minWidth: 0,
        }}
      >
        {OPS.map(({ op, label }) => (
          <button
            key={op}
            type="button"
            aria-pressed={draft.op === op}
            onClick={() => setDraft((d) => ({ ...d, op }))}
            style={opButtonStyle(draft.op === op)}
          >
            {label}
          </button>
        ))}
      </fieldset>

      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={`Search ${field.label}…`}
        aria-label={`Search ${field.label}`}
        ref={searchRef}
        style={{
          display: "block",
          width: "calc(100% - 16px)",
          boxSizing: "border-box",
          margin: "0 8px 6px",
          padding: "8px 10px",
          border: "1px solid var(--color-smoke)",
          borderRadius: 7,
          fontFamily: "var(--font-lato)",
          fontSize: 13,
          color: "var(--color-rodeo-black)",
          outline: "none",
        }}
      />

      <Box sx={{ maxHeight: 230, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {shown.length === 0 ? (
          <Box sx={{ px: "10px", py: 1, fontSize: 13, color: "var(--color-graphite)" }}>
            No matches
          </Box>
        ) : (
          shown.map((o) => (
            <FormControlLabel
              key={o.value}
              control={
                <Checkbox
                  checked={draft.values.includes(o.value)}
                  onChange={() => toggle(o.value)}
                  size="small"
                  sx={{ p: 0.5, mr: 1 }}
                />
              }
              label={o.label}
              sx={{
                mx: 0,
                px: "6px",
                py: "2px",
                borderRadius: "7px",
                "&:hover": { bgcolor: "var(--color-wax)" },
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  color: "var(--color-rodeo-black)",
                },
              }}
            />
          ))
        )}
      </Box>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          padding: "8px 4px 2px",
          borderTop: "1px solid var(--color-smoke)",
          marginTop: 4,
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={{
            ...footButton,
            background: "var(--color-white)",
            border: "1px solid var(--color-smoke)",
            color: "var(--color-rodeo-black)",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onApply(draft)}
          style={{
            ...footButton,
            background: "var(--color-rodeo-black)",
            border: "none",
            color: "var(--color-white)",
          }}
        >
          Apply
        </button>
      </div>
    </Box>
  );
}
